<template>
  <div class="sections-table-view">
    <!-- En-tête avec boutons d'action -->
    <div class="sections-header">
      <div class="sections-header-content">
        <div class="sections-header-inner">
          <h1 class="sections-title">Sections</h1>
          
          <div class="sections-actions">
            <!-- Bouton pour ajouter une section racine -->
            <button 
              class="sections-btn sections-btn-primary"
              title="Ajouter une section racine"
              @click="openAddRootSectionModal"
            >
              <PlusIcon class="sections-btn-icon" />
              <span>Section racine</span>
            </button>
            
            <!-- Boutons collapse/expand -->
            <button 
              class="sections-btn sections-btn-secondary"
              title="Replier toutes les sections"
              @click="sectionsStore.collapseAll()"
            >
              <span class="text-sm">▶ Tout replier</span>
            </button>
            <button 
              class="sections-btn sections-btn-secondary"
              title="Déplier toutes les sections"
              @click="sectionsStore.expandAll()"
            >
              <span class="text-sm">▼ Tout déplier</span>
            </button>
            
            <!-- Bouton de gestion des sections à problèmes -->
            <button 
              class="sections-btn sections-btn-warning"
              :class="{ 'sections-btn-repair-pulse': sectionsStore.problemCount > 0 }"
              title="Gérer les sections à problèmes"
              @click="openProblemsModal"
            >
              <span class="text-sm">🔧 Réparer ({{ sectionsStore.problemCount }})</span>
            </button>
            
            <!-- Bouton de nettoyage des noms corrompus -->
            <button 
              class="sections-btn sections-btn-danger"
              title="Nettoyer les noms corrompus en base de données"
              @click="cleanCorruptedNames"
            >
              <span class="text-sm">🧹 Nettoyer BD</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Contenu principal -->
    <div class="sections-main-content">
      <!-- État de chargement -->
      <div v-if="sectionsStore.loading" class="section-loading">
        <div class="section-loading-spinner"></div>
        <span class="section-loading-text">Chargement des sections...</span>
      </div>

      <!-- État d'erreur -->
      <div v-else-if="sectionsStore.error" class="section-error">
        <h3 class="section-error-title">Erreur</h3>
        <p class="section-error-message">{{ sectionsStore.error }}</p>
      </div>

      <!-- Tableau des sections -->
      <div v-else-if="sectionsStore.sections.length > 0" class="sections-table-container">
        <!-- En-tête du tableau -->
        <div class="sections-table-header">
          <div class="section-row">
            <div class="section-actions">Actions</div>
            <div class="section-hierarchy flex-grow">Nom</div>
            <div class="section-conditions-column">Conditions</div>
            <div class="section-translations-column">Traductions</div>
            <div class="section-type-column">Type</div>
            <!-- Colonnes masquées temporairement -->
            <!-- <div class="section-display-column">Afficher</div> -->
            <!-- <div class="section-mandatory-column">Obligatoire</div> -->
          </div>
        </div>
        
        <!-- Corps du tableau -->
        <div class="sections-table-body">
          <template v-for="section in sectionsStore.sections" :key="section.id">
            <!-- Masquer cette section si son parent est replié -->
            <div v-if="sectionsStore.shouldShowSection(section)" class="section-item">
              <!-- Ligne de section -->
              <div 
                class="section-row"
                :class="{ 
                  'section-row-invalid-type': !hasValidType(section),
                  'section-row-dragging': sectionsStore.draggedSection && sectionsStore.draggedSection.id === section.id,
                  'section-row-drop-before': sectionsStore.dragOverSection && sectionsStore.dragOverSection.id === section.id && sectionsStore.dropPosition === 'before',
                  'section-row-drop-after': sectionsStore.dragOverSection && sectionsStore.dragOverSection.id === section.id && sectionsStore.dropPosition === 'after',
                  'section-row-drop-child': sectionsStore.dragOverSection && sectionsStore.dragOverSection.id === section.id && sectionsStore.dropPosition === 'child'
                }"
                draggable="true"
                @mouseenter="handleMouseEnter(section)"
                @mouseleave="handleMouseLeave"
                @dragstart="startDrag(section)"
                @dragover="onDragOver(section, $event)"
                @dragleave="onDragLeave($event)"
                @drop="onDrop(section, $event)"
              >
                <!-- Actions buttons -->
                <div class="section-actions">
                  <button 
                    class="section-action-btn section-action-btn-drag" 
                    title="Déplacer la section (glisser-déposer)"
                  >
                    <span class="text-xs">|||</span>
                  </button>
                  <button 
                    class="section-action-btn section-action-btn-add" 
                    title="Ajouter une sous-section"
                    @click="addSubsection(section)"
                  >
                    <span class="text-xs">+</span>
                  </button>
                  <button 
                    class="section-action-btn section-action-btn-delete" 
                    title="Supprimer la section et ses sous-sections"
                    @click="confirmDelete(section)"
                  >
                    <span class="text-xs">🗑️</span>
                  </button>
                </div>
                
                <!-- Section content -->
                <div class="section-hierarchy">
                  <!-- Indentation et bouton collapse -->
                  <div class="section-indentation">
                    <!-- Indentation basée sur la profondeur du nom complet -->
                    <span v-if="(section.indentationLevel || 0) > 0" class="section-tree-lines">
                      <span v-for="i in (section.indentationLevel || 0)" :key="i" class="section-tree-connector">│</span>
                      └─
                    </span>
                    
                    <!-- Bouton collapse/expand pour les sections avec enfants -->
                    <button 
                      v-if="sectionsStore.hasChildren(section)"
                      class="section-collapse-btn"
                      :title="sectionsStore.isCollapsed(section.name) ? 'Déplier' : 'Replier'"
                      @click="sectionsStore.toggleCollapse(section.name)"
                    >
                      <span class="text-xs">{{ sectionsStore.isCollapsed(section.name) ? '▶' : '▼' }}</span>
                    </button>
                    <span v-else class="ml-6"></span> <!-- Espace pour l'alignement -->
                  </div>
                  
                  <!-- Section name -->
                  <div class="section-name-container" @click="startEditName(section)">
                    <input 
                      v-if="sectionsStore.editingName === section.name" 
                      ref="nameInput" 
                      v-model="sectionsStore.editingNameValue" 
                      class="section-name-input" 
                      @blur="saveEditedName(section)"
                      @keyup.enter="saveEditedName(section)"
                      @keyup.escape="cancelEditName"
                    />
                    <!-- Affichage hiérarchique propre -->
                    <div v-else class="section-name-display">
                      <!-- Afficher seulement le dernier segment du nom -->
                      <span class="section-name-text">
                        {{ getDisplayName(section.name) }}
                      </span>
                      
                      <!-- Icône d'édition au hover -->
                      <span class="section-name-edit-icon">
                        ✏️
                      </span>
                    </div>
                  </div>
                </div>
                
                <!-- Conditions column -->
                <div class="section-conditions-column">
                  <template v-if="section.fieldConfigs && section.fieldConfigs.length > 0">
                    <div
                      class="relative cursor-pointer"
                      @mouseenter="hoveredConfigSectionId = section.id"
                      @mouseleave="hoveredConfigSectionId = null"
                    >
                      <span
                        class="section-conditions-badge"
                        @click="section.fieldConfigs && section.fieldConfigs.length === 1 ? openConditionModal(section, section.fieldConfigs[0]) : null"
                      >
                        {{ section.fieldConfigs.length }}
                      </span>
                      <!-- Tooltip for conditions -->
                      <div
                        v-if="hoveredConfigSectionId === section.id"
                        class="section-tooltip section-tooltip-condition"
                        @mouseenter="hoveredConfigSectionId = section.id"
                        @mouseleave="hoveredConfigSectionId = null"
                      >
                        <div
                          v-for="config in section.fieldConfigs"
                          :key="config.id"
                          class="mb-1 last:mb-0 hover:bg-gray-100 p-1 rounded cursor-pointer"
                          @mousedown.prevent="openConditionModal(section, config)"
                        >
                          {{ getConditionText(config) }}
                        </div>
                      </div>
                    </div>
                  </template>
                  <button 
                    class="section-action-btn section-action-btn-condition"
                    title="Ajouter une condition"
                    @click="openConditionModal(section)"
                  >
                    <PlusIcon class="w-4 h-4" />
                  </button>
                </div>
                
                <!-- Translations column -->
                <div class="section-translations-column">
                  <template v-if="section.translations && section.translations.length > 0">
                    <div 
                      v-for="translation in section.translations" 
                      :key="translation.id"
                      class="relative cursor-pointer"
                      @mouseenter="hoveredTranslation = translation"
                      @mouseleave="hoveredTranslation = null"
                      @click="editTranslation(translation)"
                    >
                      <span :class="`fi fi-${getFlagCode(translation.language_code)}`" class="section-flag"></span>
                      
                      <!-- Tooltip for translation -->
                      <div 
                        v-if="hoveredTranslation === translation"
                        class="section-tooltip section-tooltip-translation"
                      >
                        <div class="font-medium mb-1">{{ translation.titre }}</div>
                        <div class="text-sm text-gray-600">{{ translation.description }}</div>
                      </div>
                    </div>
                  </template>
                  <button 
                    class="section-action-btn section-action-btn-translation"
                    title="Ajouter une traduction"
                    @click="openTranslationModal(section)"
                  >
                    <PlusIcon class="w-4 h-4" />
                  </button>
                </div>
                
                <!-- Type selector -->
                <div class="section-type-column">
                  <select 
                    v-model="section.type" 
                    class="section-type-select" 
                    @change="updateSectionType(section)"
                  >
                    <option value="">-</option>
                    <template v-if="isFinalSection(section)">
                      <!-- Types pour sections finales -->
                      <option value="text">text</option>
                      <option value="textarea">textarea</option>
                      <option value="bool">bool</option>
                    </template>
                    <template v-else>
                      <!-- Types pour sections conteneurs -->
                      <option value="collapse">collapse</option>
                      <option value="dropdown">dropdown</option>
                    </template>
                  </select>
                </div>
                
                <!-- Colonnes masquées temporairement -->
                <!-- Display checkbox -->
                <!-- <div class="section-display-column">
                  <input 
                    v-model="section.display" 
                    type="checkbox" 
                    class="section-checkbox" 
                    @change="updateSection(section.id)"
                  />
                </div> -->
                
                <!-- Mandatory checkbox -->
                <!-- <div class="section-mandatory-column">
                  <input 
                    v-model="section.mandatory_entry" 
                    type="checkbox" 
                    class="section-checkbox" 
                    @change="updateSection(section.id)"
                  />
                </div> -->
              </div>

              
              <!-- New subsection input -->
              <div v-if="newSubsectionParentId === section.id" class="new-subsection ml-24 p-2 bg-gray-50 rounded">
                <div class="flex items-center">
                  <span class="text-gray-400 mr-2">
                    <span v-for="i in (section.indentationLevel || 0) + 1" :key="i" class="ml-4">│</span>
                    └─
                  </span>
                  <input 
                    ref="newSubsectionInput" 
                    v-model="newSubsection.name" 
                    class="section-name-input"
                    placeholder="Nom de la nouvelle sous-section"
                    @keyup.enter="createSubsection"
                    @blur="cancelNewSubsection"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- État vide -->
      <div v-else class="section-empty">
        <div class="section-empty-icon">📁</div>
        <h3 class="section-empty-title">Aucune section</h3>
        <p class="section-empty-description">Commencez par créer une section racine.</p>
      </div>
    </div>
    
    <!-- Tooltip pour les sections avec type invalide -->
    <div 
      v-if="hoveredSection && !hasValidType(hoveredSection)" 
      class="section-tooltip section-tooltip-warning"
      :style="tooltipStyle"
    >
      {{ getTypeWarningMessage(hoveredSection) }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, nextTick } from 'vue';
import { useSectionsStore } from '@/stores/sectionsStore';
// import { useAdminNotifications } from '@/composables/useAdminPermissions';
import type { SectionExt, FieldConfig } from '@/stores/sectionsStore';
import type { Translation } from '@/api/types/core/translations';
import { SectionUtils } from '@/utils/sectionUtils';
import { PlusIcon } from 'lucide-vue-next';
import '/node_modules/flag-icons/css/flag-icons.min.css';

export default defineComponent({
  name: 'SectionsTableView',
  components: {
    PlusIcon,
  },
  emits: [
    'open-add-root-modal',
    'open-problems-modal',
    'open-translation-modal',
    'open-condition-modal',
    'confirm-delete',
    'edit-translation',
    'show-notification'
  ],
  setup(_, { emit }) {
    const sectionsStore = useSectionsStore();
    // const { prepareErrorNotification } = useAdminNotifications();
    
    // État local
    const hoveredSection = ref<SectionExt | null>(null);
    const hoveredTranslation = ref<Translation | null>(null);
    const hoveredConfigSectionId = ref<number | null>(null);
    const newSubsectionParentId = ref<number | null>(null);
    const newSubsection = ref({
      name: "",
      parent_id: null as number | null,
      type: "",
      display: true,
      ordre: 0,
      importance: 0,
      mandatory_entry: false,
    });
    
    const tooltipStyle = ref({
      top: '0px',
      left: '0px'
    });

    // Computed properties
    const availableTypes = computed(() => ["", "text", "textarea", "bool", "collapse", "dropdown"]);

    // Méthodes utilitaires
    const getDisplayName = (fullName: string): string => {
      return SectionUtils.getLastSegment(fullName);
    };

    const isFinalSection = (section: SectionExt): boolean => {
      const sectionFullName = section.name;
      return !sectionsStore.sections.some(s => s.name.startsWith(sectionFullName + '.'));
    };

    const hasValidType = (section: SectionExt): boolean => {
      const isFinal = isFinalSection(section);
      
      if (isFinal) {
        return Boolean(section.type && ['text', 'textarea', 'bool'].includes(section.type));
      } else {
        return !section.type || ['collapse', 'dropdown'].includes(section.type);
      }
    };

    const getTypeWarningMessage = (section: SectionExt): string => {
      const isFinal = isFinalSection(section);
      
      if (isFinal && !section.type) {
        return "Cette section finale doit avoir un type de champ (text, textarea, bool).";
      } else if (isFinal && section.type && ['collapse', 'dropdown'].includes(section.type)) {
        return "Les types 'collapse' et 'dropdown' ne peuvent pas être appliqués aux sections finales.";
      } else if (!isFinal && section.type && ['text', 'textarea', 'bool'].includes(section.type)) {
        return "Les types de champs ne peuvent pas être appliqués aux sections qui contiennent des sous-sections.";
      }
      
      return "";
    };

    const getFlagCode = (languageCode: string): string => {
      const flagMap: { [key: string]: string } = {
        'en': 'gb',
        'fr': 'fr',
        'de': 'de',
      };
      return flagMap[languageCode] || languageCode;
    };

    const getConditionText = (config: FieldConfig): string => {
      const conditionSection = sectionsStore.sections.find(s => s.id === config.condition_id);
      return conditionSection 
        ? `Si ${conditionSection.name} est ${config.condition_value}`
        : "Section conditionnelle non trouvée";
    };

    const getNextOrder = (parentFullName: string): number => {
      const children = sectionsStore.sections.filter(s => {
        const parentPrefix = parentFullName + '.';
        return s.name.startsWith(parentPrefix) && 
               !s.name.substring(parentPrefix.length).includes('.');
      });
      
      if (children.length === 0) return 1;
      
      const ordres = children.map(s => s.ordre ?? 0);
      return Math.max(...ordres) + 1;
    };

    // Actions
    const openAddRootSectionModal = (): void => {
      emit('open-add-root-modal');
    };

    const openProblemsModal = (): void => {
      emit('open-problems-modal');
    };

    const openTranslationModal = (section: SectionExt): void => {
      emit('open-translation-modal', section);
    };

    const openConditionModal = (section: SectionExt, config?: FieldConfig): void => {
      emit('open-condition-modal', section, config);
    };

    const editTranslation = (translation: Translation): void => {
      emit('edit-translation', translation);
    };

    const confirmDelete = (section: SectionExt): void => {
      emit('confirm-delete', section);
    };

    const showNotification = (message: string, type: string): void => {
      emit('show-notification', message, type);
    };

    // Gestion des sous-sections
    const addSubsection = (parentSection: SectionExt): void => {
      newSubsectionParentId.value = parentSection.id;
      newSubsection.value = {
        name: "",
        parent_id: parentSection.id,
        type: "",
        display: true,
        ordre: getNextOrder(parentSection.name),
        importance: 0,
        mandatory_entry: false,
      };
      
      nextTick(() => {
        const inputs = document.querySelectorAll('input[ref="newSubsectionInput"]');
        const lastInput = inputs[inputs.length - 1] as HTMLInputElement;
        if (lastInput) {
          lastInput.focus();
          lastInput.select();
        }
      });
    };

    const createSubsection = async (): Promise<void> => {
      if (!newSubsection.value.name.trim()) {
        showNotification("Le nom de la section ne peut pas être vide.", "error");
        return;
      }
      
      try {
        const parentSection = sectionsStore.sections.find(s => s.id === newSubsectionParentId.value);
        if (!parentSection) {
          showNotification("Section parent introuvable.", "error");
          return;
        }
        
        const simpleName = newSubsection.value.name.trim();
        if (!SectionUtils.isValidSectionName(simpleName) || simpleName.includes('.')) {
          showNotification("Nom de section invalide. Utilisez uniquement des lettres, chiffres et underscores, sans point.", "error");
          return;
        }
        
        const fullName = `${parentSection.name}.${simpleName}`;
        
        if (sectionsStore.sections.some(s => s.name === fullName)) {
          showNotification("Une section avec ce nom existe déjà.", "error");
          return;
        }
        
        const sectionToCreate = {
          name: fullName,
          type: newSubsection.value.type || 'text', // Type par défaut pour les nouvelles sections
          display: newSubsection.value.display,
          ordre: getNextOrder(parentSection.name),
          importance: newSubsection.value.importance || 0,
          mandatory_entry: newSubsection.value.mandatory_entry || false
        };
        
        const result = await sectionsStore.createSection(sectionToCreate);
        if (result.success) {
          // Auto-ajuster le type du parent si nécessaire
          await sectionsStore.autoSetSectionType(parentSection);
          
          showNotification("Sous-section ajoutée avec succès.", "success");
          cancelNewSubsection();
        } else {
          showNotification("Erreur lors de l'ajout de la sous-section.", "error");
        }
        
      } catch (error) {
        console.error('❌ Erreur création sous-section:', error);
        showNotification("Erreur lors de l'ajout de la sous-section.", "error");
      }
    };

    const cancelNewSubsection = (): void => {
      newSubsectionParentId.value = null;
      newSubsection.value = {
        name: "",
        parent_id: null,
        type: "",
        display: true,
        ordre: 0,
        importance: 0,
        mandatory_entry: false,
      };
    };

    // Gestion de l'édition des noms
    const startEditName = (section: SectionExt): void => {
      sectionsStore.editingName = section.name;
      sectionsStore.editingNameValue = getDisplayName(section.name);
      
      nextTick(() => {
        const input = document.querySelector('input[ref="nameInput"]') as HTMLInputElement;
        if (input) {
          input.focus();
          input.select();
        }
      });
    };

    const saveEditedName = async (section: SectionExt): Promise<void> => {
      if (!sectionsStore.editingName || !sectionsStore.editingNameValue.trim()) {
        cancelEditName();
        return;
      }
      
      try {
        const newSimpleName = sectionsStore.editingNameValue.trim();
        if (!SectionUtils.isValidSectionName(newSimpleName) || newSimpleName.includes('.')) {
          showNotification("Nom invalide. Utilisez uniquement des lettres, chiffres et underscores, sans point.", "error");
          return;
        }
        
        const parentName = SectionUtils.getParentName(section.name);
        const newFullName = parentName ? `${parentName}.${newSimpleName}` : newSimpleName;
        
        if (newFullName !== section.name && sectionsStore.sections.some(s => s.name === newFullName)) {
          showNotification("Une section avec ce nom existe déjà.", "error");
          return;
        }
        
        // Utiliser la méthode optimisée du store pour renommer en cascade
        const renameResult = await sectionsStore.renameWithCascade(section.name, newFullName);
        
        if (renameResult.success) {
          // ✅ Plus besoin de rechargement, renameWithCascade met à jour l'état local
          showNotification("Section et sous-sections renommées avec succès.", "success");
        } else {
          console.error('❌ Erreur renommage:', renameResult.error);
          showNotification("Erreur lors de la mise à jour du nom de section.", "error");
        }
        
      } catch (error) {
        console.error('❌ Erreur mise à jour nom:', error);
        showNotification("Erreur lors de la mise à jour du nom de section.", "error");
      } finally {
        cancelEditName();
      }
    };

    const cancelEditName = (): void => {
      sectionsStore.editingName = null;
      sectionsStore.editingNameValue = '';
    };

    // Gestion des mises à jour
    const updateSection = async (sectionId: number): Promise<void> => {
      const section = sectionsStore.sections.find(s => s.id === sectionId);
      if (section) {
        const result = await sectionsStore.updateSection(sectionId, section);
        if (result.success) {
          showNotification("Section mise à jour avec succès.", "success");
        } else {
          showNotification("Erreur lors de la mise à jour de la section.", "error");
        }
      }
    };

    const updateSectionType = async (section: SectionExt): Promise<void> => {
      const isFinal = isFinalSection(section);
      
      if (isFinal && section.type && ['collapse', 'dropdown'].includes(section.type)) {
        section.type = "";
        showNotification("Les types 'collapse' et 'dropdown' ne peuvent pas être appliqués aux sections finales.", "warning");
        return;
      }
      
      if (!isFinal && section.type && ['text', 'textarea', 'bool'].includes(section.type)) {
        section.type = "";
        showNotification("Les types de champs ne peuvent pas être appliqués aux sections qui contiennent des sous-sections.", "warning");
        return;
      }
      
      const result = await sectionsStore.updateSection(section.id, {
        name: section.name,
        type: section.type,
        display: section.display,
        ordre: section.ordre,
        importance: section.importance,
        mandatory_entry: section.mandatory_entry
      });
      
      if (result.success) {
        showNotification("Type de section mis à jour avec succès.", "success");
      } else {
        showNotification("Erreur lors de la mise à jour du type de section.", "error");
      }
    };

    // Gestion du drag & drop
    const startDrag = (section: SectionExt): void => {
      console.log('🎯 startDrag:', section.name);
      sectionsStore.draggedSection = section;
    };

    const onDragOver = (section: SectionExt, event: DragEvent): void => {
      event.preventDefault();
      
      // Éviter les oscillations en ne changeant que si nécessaire
      if (sectionsStore.dragOverSection?.id === section.id) {
        // Même section, juste recalculer la position
        const rect = (event.target as HTMLElement).getBoundingClientRect();
        const y = event.clientY - rect.top;
        const height = rect.height;
        
        // Zones stables sans oscillation
        const newPosition = y < height * 0.3 ? 'before' : 
                           y > height * 0.7 ? 'after' : 'child';
        
        if (sectionsStore.dropPosition !== newPosition) {
          sectionsStore.dropPosition = newPosition;
        }
      } else {
        // Nouvelle section
        sectionsStore.dragOverSection = section;
        
        const rect = (event.target as HTMLElement).getBoundingClientRect();
        const y = event.clientY - rect.top;
        const height = rect.height;
        
        sectionsStore.dropPosition = y < height * 0.3 ? 'before' : 
                                    y > height * 0.7 ? 'after' : 'child';
      }
    };

    const onDragLeave = (event: DragEvent): void => {
      // Vérifier si on quitte vraiment l'élément (pas juste un enfant)
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;
      
      // Si la souris est encore dans les limites de l'élément, ne pas nettoyer
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        return;
      }
      
      // Délai minimal pour éviter les clignotements
      setTimeout(() => {
        if (!sectionsStore.draggedSection) return;
        sectionsStore.dragOverSection = null;
        sectionsStore.dropPosition = 'after';
      }, 30);
    };

    const onDrop = async (section: SectionExt, event: DragEvent): Promise<void> => {
      event.preventDefault();
      
      console.log('🎯 onDrop appelé:', {
        section: section.name,
        draggedSection: sectionsStore.draggedSection?.name,
        dragOverSection: sectionsStore.dragOverSection?.name,
        dropPosition: sectionsStore.dropPosition
      });
      
      if (!sectionsStore.draggedSection || !sectionsStore.dragOverSection) {
        console.log('❌ Pas de section draggedSection ou dragOverSection');
        return;
      }
      
      if (sectionsStore.draggedSection.id === sectionsStore.dragOverSection.id) {
        console.log('❌ Même section, annulation');
        sectionsStore.draggedSection = null;
        sectionsStore.dragOverSection = null;
        sectionsStore.dropPosition = 'after';
        return;
      }
      
      try {
        console.log('🚀 Appel performDrop...');
        const result = await sectionsStore.performDrop(
          sectionsStore.draggedSection,
          sectionsStore.dragOverSection,
          sectionsStore.dropPosition
        );
        
        console.log('📊 Résultat performDrop:', result);
        
        if (result.success) {
          showNotification("Section déplacée avec succès.", "success");
        } else {
          showNotification("Erreur lors du déplacement de la section.", "error");
        }
      } catch (error) {
        console.error('❌ Erreur drag & drop:', error);
        showNotification("Erreur lors du déplacement de la section.", "error");
      } finally {
        // Reset l'état drag & drop
        sectionsStore.draggedSection = null;
        sectionsStore.dragOverSection = null;
        sectionsStore.dropPosition = 'after';
      }
    };

    // Gestion du hover optimisée pour éviter les conflits avec le drag & drop
    const handleMouseEnter = (section: SectionExt): void => {
      // Ne pas changer le hover pendant un drag & drop
      if (!sectionsStore.draggedSection) {
        hoveredSection.value = section;
      }
    };

    const handleMouseLeave = (): void => {
      // Ne pas changer le hover pendant un drag & drop
      if (!sectionsStore.draggedSection) {
        hoveredSection.value = null;
      }
    };

    // Gestion du tooltip
    const updateTooltipPosition = (event: MouseEvent): void => {
      const offset = 10;
      tooltipStyle.value = {
        top: `${event.clientY + offset}px`,
        left: `${event.clientX + offset}px`
      };
    };

    // Lifecycle
    window.addEventListener('mousemove', updateTooltipPosition);

    // Nettoyage des noms corrompus
    const cleanCorruptedNames = async (): Promise<void> => {
      try {
        showNotification("Nettoyage des noms corrompus en cours...", "info");
        const result = await sectionsStore.cleanAllCorruptedNames();
        
        if (result.success) {
          if (result.cleanedCount > 0) {
            showNotification(`✅ ${result.cleanedCount} nom(s) corrompu(s) corrigé(s) dans la base de données.`, "success");
          } else {
            showNotification("✅ Aucun nom corrompu détecté. La base de données est propre.", "success");
          }
        } else {
          showNotification("❌ Erreur lors du nettoyage des noms corrompus.", "error");
        }
      } catch (error) {
        console.error('❌ Erreur nettoyage noms corrompus:', error);
        showNotification("❌ Erreur lors du nettoyage des noms corrompus.", "error");
      }
    };

    return {
      sectionsStore,
      hoveredSection,
      hoveredTranslation,
      hoveredConfigSectionId,
      newSubsectionParentId,
      newSubsection,
      tooltipStyle,
      availableTypes,
      
      // Méthodes utilitaires
      getDisplayName,
      isFinalSection,
      hasValidType,
      getTypeWarningMessage,
      getFlagCode,
      getConditionText,
      getNextOrder,
      
      // Actions
      openAddRootSectionModal,
      openProblemsModal,
      openTranslationModal,
      openConditionModal,
      editTranslation,
      confirmDelete,
      showNotification,
      
      // Gestion des sous-sections
      addSubsection,
      createSubsection,
      cancelNewSubsection,
      
      // Gestion de l'édition
      startEditName,
      saveEditedName,
      cancelEditName,
      
      // Gestion des mises à jour
      updateSection,
      updateSectionType,
      
      // Gestion du drag & drop
      startDrag,
      onDragOver,
      onDragLeave,
      onDrop,
      
      // Gestion du hover
      handleMouseEnter,
      handleMouseLeave,
      
      // Nettoyage des noms corrompus
      cleanCorruptedNames,
    };
  }
});
</script>

<style>
/* ===================================
   STYLES POUR LE DRAG & DROP
   ================================== */

/* Styles pour les zones de drop - sans déplacement pour éviter les oscillations */
.section-row-drop-before {
  border-top: 3px solid #3b82f6 !important;
  background-color: rgba(59, 130, 246, 0.05) !important;
}

.section-row-drop-after {
  border-bottom: 3px solid #3b82f6 !important;
  background-color: rgba(59, 130, 246, 0.05) !important;
}

.section-row-drop-child {
  background-color: rgba(59, 130, 246, 0.08) !important;
  border-left: 4px solid #3b82f6 !important;
  /* Suppression du transform qui causait les oscillations */
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.15);
}

/* Section en cours de déplacement - style subtil sans déformation */
.section-row-dragging {
  opacity: 0.6;
  background-color: #f1f5f9 !important;
  border: 1px dashed #94a3b8 !important;
  /* Suppression des transformations qui peuvent causer des problèmes */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  position: relative;
}

/* Curseur pendant le drag */
.section-row[draggable="true"]:hover {
  cursor: grab;
}

.section-row[draggable="true"]:active {
  cursor: grabbing;
}

/* Animation pour les changements de position */
.section-row {
  transition: all 0.15s ease-out;
  border-radius: 0.25rem;
}

/* Amélioration des transitions pour le drag & drop */
.section-row-drop-before,
.section-row-drop-after,
.section-row-drop-child {
  transition: all 0.1s ease-out !important;
}

/* Highlight pour les zones de drop valides */
.section-row-drop-valid {
  background-color: #f0f9ff;
  border-left: 4px solid #0ea5e9;
}

/* Highlight pour les zones de drop invalides */
.section-row-drop-invalid {
  background-color: #fef2f2;
  border-left: 4px solid #ef4444;
}
</style> 